import { useState } from "react";
import Box from "@mui/material/Box";
import DownloadIcon from "@mui/icons-material/Download";
import { ButtonCustom } from "../Botoes/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { useTheme } from "@mui/material/styles";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

// Função para formatar o tamanho do arquivo
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export default function DocumentoCard({ documento }) {
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);
  
  // Obter informações do arquivo
  const getFileInfo = () => {
    const fileType = documento.base64.split(";")[0].split("/")[1];
    const fileName = documento.descricao === null
      ? documento.tipoDocumento === null
        ? "Documento"
        : documento.tipoDocumento.descricao
      : documento.descricao;
    
    return { fileType, fileName };
  };

  const { fileType, fileName } = getFileInfo();
  
  // Obter ícone apropriado baseado no tipo de arquivo
  const getFileIcon = () => {
    switch (fileType) {
      case "pdf":
        return <PictureAsPdfIcon sx={{ color: "#f40f02", fontSize: 32 }} />;
      case "txt":
        return <TextSnippetIcon sx={{ color: "#1976d2", fontSize: 32 }} />;
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
        return <ImageIcon sx={{ color: "#2e7d32", fontSize: 32 }} />;
      default:
        return <InsertDriveFileIcon sx={{ color: "#757575", fontSize: 32 }} />;
    }
  };

  // Formatar tamanho do arquivo
  const formattedSize = formatFileSize(documento.tamanho * 1024);

  // Tratar erro de carregamento de imagem
  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Card
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            transition: "all 0.2s ease-in-out",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            overflow: "hidden",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: theme.shadows[2],
              borderColor: theme.palette.primary.light,
            },
          }}
        >
          <Box
            component="a"
            href={documento.base64}
            download={`${fileName}.${fileType}`}
            sx={{
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
            }}
          >
            {/* Preview compacto do arquivo */}
            <CardContent 
              sx={{ 
                p: 1, 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                backgroundColor: theme.palette.grey[50],
                minHeight: 100,
              }}
            >
              {(fileType === "pdf" || fileType === "txt" || imageError) ? (
                <Box sx={{ textAlign: "center" }}>
                  {getFileIcon()}
                </Box>
              ) : (
                <Box
                  component="img"
                  src={documento.base64}
                  onError={handleImageError}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    maxHeight: 100,
                  }}
                  alt={fileName}
                />
              )}
            </CardContent>

            {/* Informações compactas do arquivo */}
            <Box sx={{ p: 1.5, flexGrow: 1 }}>
              <Tooltip title={fileName}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    lineHeight: 1.3,
                  }}
                >
                  {fileName}
                </Typography>
              </Tooltip>
              
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {formattedSize}
                </Typography>
                
                <ButtonCustom
                  variant="text"
                  size="small"
                  sx={{
                    minWidth: "auto",
                    p: 0.5,
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <DownloadIcon sx={{ fontSize: 18 }} />
                </ButtonCustom>
              </Box>
            </Box>
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
}